import PropTypes from 'prop-types';
import { useProperty, useSiteContext, useTranslation } from '@eti/providers';
import { css } from '@eti/styles';
import { price as priceUtil } from '@eti/utils';
import { ScreenReaderPrice } from '@eti/components';
import { getAgeTypeLabel, getAgeTypeLabelForQuantity } from '../../../../utils/travelers';
import { CartRow, Price } from '../../../../widgets/cart/components/CartComponents';

const priceSummaryStyles = css`
  font-weight: 700;
`;

const isShowingPrice = (price) => Boolean(price);

const PriceBreakdownTravelerGroup = ({
  ageType,
  isRyanAirTicketingOrMarketingCarrierPresent,
  isVI,
  marketingCarrierNames,
  markup,
  price,
  quantity,
  totalPrice,
  vat,
}) => {
  const { t } = useTranslation();
  const { p } = useProperty();
  const { brand } = useSiteContext();
  const isCFCEnabled = p('IbeClient.CarrierFlightConfirmation.Enabled');
  const travelerTypeLabel = getAgeTypeLabelForQuantity(ageType, quantity);
  const ageTypeLabel = t(getAgeTypeLabel(ageType)).toLowerCase();
  const costLabel = t('Cart.CostPerAgeType', ageTypeLabel);
  const discountLabel = t('Cart.DiscountPerAgeType', ageTypeLabel);
  const travelerTypeCount = t(travelerTypeLabel, quantity);

  const getVatLabels = () => {
    if (marketingCarrierNames) {
      if (isRyanAirTicketingOrMarketingCarrierPresent && isCFCEnabled) {
        return t(
          'Cart.TaxesAndChargesPerAgeType.WithCarriers.RyanairSpecific',
          ageTypeLabel,
          marketingCarrierNames,
        );
      }
      return t('Cart.TaxesAndChargesPerAgeType.WithCarriers', ageTypeLabel, marketingCarrierNames);
    }
    return t('Cart.TaxesAndChargesPerAgeType', ageTypeLabel);
  };

  const getPriceByAgeLabels = () => {
    if (
      marketingCarrierNames &&
      isRyanAirTicketingOrMarketingCarrierPresent &&
      isCFCEnabled &&
      !isVI
    ) {
      return t('Cart.PricePerAgeType.WithCarrierName', ageTypeLabel, marketingCarrierNames);
    }
    return t('Cart.PricePerAgeType', ageTypeLabel);
  };

  const capitalizeFirstLetter = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  const getMarkupByAgeLabels = () => {
    if (marketingCarrierNames && isRyanAirTicketingOrMarketingCarrierPresent && isCFCEnabled) {
      if (markup >= 0) {
        return t(
          'Cart.CostPerAgeType.WithBrandName',
          capitalizeFirstLetter(brand.code),
          ageTypeLabel,
        );
      }
      return t(
        'Cart.DiscountPerAgeType.WithBrandName',
        capitalizeFirstLetter(brand.code),
        ageTypeLabel,
      );
    }
    return markup >= 0 ? costLabel : discountLabel;
  };

  const shouldShowVatPrice = isShowingPrice(vat);
  const shouldShowMarkupPrice = isShowingPrice(markup);
  const formattedPrice = priceUtil.formatPrice(price);
  const formattedVatPrice = shouldShowVatPrice ? priceUtil.formatPrice(vat) : '';
  const formattedMarkupPrice = shouldShowMarkupPrice ? priceUtil.formatPrice(markup) : '';
  const formattedTotalPrice = priceUtil.formatPrice(totalPrice);

  return (
    <div>
      <CartRow className={priceSummaryStyles}>
        <span data-testid={`trip-priceBreakdown-ageTypeCount-${ageType}`}>{travelerTypeCount}</span>
      </CartRow>
      <CartRow>
        <span data-testid={`trip-priceBreakdown-pricePerAgeType-${ageType}-label`}>
          {getPriceByAgeLabels()}
        </span>
        <Price aria-hidden data-testid={`trip-priceBreakdown-pricePerAgeType-${ageType}`}>
          {formattedPrice}
        </Price>
        <ScreenReaderPrice price={formattedPrice} />
      </CartRow>
      {shouldShowVatPrice && (
        <CartRow>
          <span data-testid={`trip-priceBreakdown-vat-${ageType}-label`}>{getVatLabels()}</span>
          <Price aria-hidden data-testid={`trip-priceBreakdown-vat-${ageType}`}>
            {formattedVatPrice}
          </Price>
          <ScreenReaderPrice price={formattedVatPrice} />
        </CartRow>
      )}
      {shouldShowMarkupPrice && (
        <CartRow>
          <span data-testid={`trip-priceBreakdown-markup-${ageType}-label`}>
            {getMarkupByAgeLabels()}
          </span>
          <Price aria-hidden data-testid={`trip-priceBreakdown-markup-${ageType}`}>
            {formattedMarkupPrice}
          </Price>
          <ScreenReaderPrice price={formattedMarkupPrice} />
        </CartRow>
      )}
      <CartRow className={priceSummaryStyles}>
        <span data-testid={`trip-priceBreakdown-totalForAgeType-${ageType}-label`}>
          {t('TripDetails.PriceBreakdown.TravelerGroupPriceSummary', travelerTypeCount)}
        </span>
        <Price aria-hidden data-testid={`trip-priceBreakdown-totalForAgeType-${ageType}`}>
          {formattedTotalPrice}
        </Price>
        <ScreenReaderPrice price={formattedTotalPrice} />
      </CartRow>
    </div>
  );
};

PriceBreakdownTravelerGroup.propTypes = {
  ageType: PropTypes.string,
  isRyanAirTicketingOrMarketingCarrierPresent: PropTypes.bool,
  isVI: PropTypes.bool,
  marketingCarrierNames: PropTypes.string,
  markup: PropTypes.number,
  price: PropTypes.number,
  quantity: PropTypes.number,
  totalPrice: PropTypes.number,
  vat: PropTypes.number,
};

export default PriceBreakdownTravelerGroup;
